import React, { useState } from 'react';
import axios from 'axios';
import {
  AlertCircle, CreditCard, Wallet, Globe, Loader2, IndianRupee,
} from 'lucide-react';
import { SUPPORT_API } from './supportConstants';
import { openRazorpayCheckout } from '../../utils/razorpayCheckout';

const API = SUPPORT_API;

export function PendingPaymentBanner({ ticketRef, payment, onDone }) {
  const [busy, setBusy] = useState(null); // 'wallet_cash' | 'wallet_credit' | 'payment_gateway'
  const [err, setErr] = useState('');

  const allowed = payment.allowed_methods || [];

  const payWallet = async () => {
    setBusy('wallet_cash'); setErr('');
    try {
      await axios.post(`${API}/support/payment-requests/${payment.id}/pay-wallet`, {}, { withCredentials: true });
      await onDone?.();
    } catch (e) { setErr(e.response?.data?.detail || 'Wallet payment failed'); }
    setBusy(null);
  };
  const payCredit = async () => {
    setBusy('wallet_credit'); setErr('');
    try {
      await axios.post(`${API}/support/payment-requests/${payment.id}/pay-credit`, {}, { withCredentials: true });
      await onDone?.();
    } catch (e) { setErr(e.response?.data?.detail || 'Credit payment failed'); }
    setBusy(null);
  };
  const payGateway = async () => {
    setBusy('payment_gateway'); setErr('');
    try {
      const r = await axios.post(`${API}/support/payment-requests/${payment.id}/initiate-gateway`, {}, { withCredentials: true });
      const d = r.data || {};
      if (!d.success) {
        setErr('Gateway not fully configured. Please contact support.');
        setBusy(null);
        return;
      }
      await openRazorpayCheckout({
        createOrderPayload: {
          context: 'support',
          receipt: d.receipt || d.order_id,
          amount: Number(d.amount),
          metadata: d.metadata || {},
        },
        prefill: d.prefill || {},
        description: `Support payment · ${ticketRef || ''}`,
        onVerified: async () => {
          await onDone?.();
        },
      });
    } catch (e) {
      if (e?.message !== 'Payment cancelled') {
        setErr(e.response?.data?.detail || e.message || 'Could not start gateway payment');
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-xl p-4 mb-3" data-testid="pending-payment-banner">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> Payment Requested
          </div>
          <div className="text-xl font-bold text-gray-900 mt-1">₹{Number(payment.amount).toLocaleString()}</div>
          {payment.notes && <div className="text-xs text-gray-700 mt-1 italic max-w-md">{payment.notes}</div>}
        </div>
        <span className="text-[10px] bg-amber-200 text-amber-900 px-2 py-0.5 rounded uppercase font-semibold">Pending</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
        {allowed.includes('wallet_cash') && (
          <button
            onClick={payWallet}
            disabled={!!busy}
            data-testid="pay-wallet-btn"
            className="flex items-center justify-center gap-2 bg-white border border-emerald-200 hover:bg-emerald-50 text-emerald-700 text-sm font-medium rounded-lg py-2.5 disabled:opacity-50"
          >
            {busy === 'wallet_cash' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
            Pay via Cash Wallet
          </button>
        )}
        {allowed.includes('wallet_credit') && (
          <button
            onClick={payCredit}
            disabled={!!busy}
            data-testid="pay-credit-btn"
            className="flex items-center justify-center gap-2 bg-white border border-indigo-200 hover:bg-indigo-50 text-indigo-700 text-sm font-medium rounded-lg py-2.5 disabled:opacity-50"
          >
            {busy === 'wallet_credit' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Pay via Credit
          </button>
        )}
        {allowed.includes('payment_gateway') && (
          <button
            onClick={payGateway}
            disabled={!!busy}
            data-testid="pay-gateway-btn"
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:opacity-90 text-white text-sm font-medium rounded-lg py-2.5 disabled:opacity-50"
          >
            {busy === 'payment_gateway' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
            Pay Online
          </button>
        )}
      </div>
      {err && <div className="text-xs text-red-600 mt-2" data-testid="pay-error">{err}</div>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  PAYMENTS PANEL — shared between Admin + User ticket detail
 * ═══════════════════════════════════════════════════════════════════════════ */
export function PaymentsPanel({ payments, onCancel, isAdmin = false }) {
  const list = payments || [];
  const fmt = (d) => d ? new Date(d).toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }) : '—';
  const methodLabel = {
    wallet_cash: 'Cash Wallet',
    wallet_credit: 'Credit Ledger',
    payment_gateway: 'Gateway',
    offline_manual: 'Offline / Manual',
  };
  const statusTone = {
    pending: 'bg-amber-100 text-amber-700',
    paid: 'bg-emerald-100 text-emerald-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-slate-100 text-slate-600',
    refunded: 'bg-emerald-100 text-emerald-700',
  };

  if (list.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="payments-panel">
        <div className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
          <IndianRupee className="w-3.5 h-3.5" /> Payments
        </div>
        <div className="text-xs text-gray-500">No payment movements on this ticket yet.</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="payments-panel">
      <div className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
        <IndianRupee className="w-3.5 h-3.5" /> Payments ({list.length})
      </div>
      <div className="space-y-2">
        {list.map((p) => (
          <div key={p.id} className="border border-gray-100 rounded-lg p-2.5 text-xs" data-testid={`payment-row-${p.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                  p.type === 'REFUND' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {p.type === 'REFUND' ? 'REFUND' : 'COLLECT'}
                </span>
                <div className="font-semibold text-gray-900">₹{Number(p.amount).toLocaleString()}</div>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${statusTone[p.status] || 'bg-gray-100'}`}>
                {p.status}
              </span>
            </div>
            <div className="mt-1 text-gray-600 space-y-0.5">
              {p.method && <div>Method: <strong>{methodLabel[p.method] || p.method}</strong></div>}
              {p.status === 'pending' && p.allowed_methods?.length > 0 && (
                <div className="text-[11px]">Allowed: {p.allowed_methods.map(m => methodLabel[m]).join(', ')}</div>
              )}
              {p.reference && <div className="font-mono text-[11px] truncate">Ref: {p.reference}</div>}
              {p.notes && <div className="italic text-gray-500 line-clamp-2">{p.notes}</div>}
              <div className="text-[11px] text-gray-400">
                Created {fmt(p.created_at)} · by {p.initiated_by_name || '—'}
                {p.paid_at && <> · paid {fmt(p.paid_at)}</>}
              </div>
              {isAdmin && p.status === 'pending' && p.type === 'ADDITIONAL_COLLECTION' && (
                <button
                  onClick={() => onCancel && onCancel(p.id)}
                  data-testid={`cancel-payreq-${p.id}`}
                  className="mt-1 text-[11px] text-red-600 hover:underline"
                >
                  Cancel request
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentsPanel;
